import { createColumnHelper } from '@tanstack/react-table';
import { CashierPastShift, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { dummyAmount } from '@mews-commander/utils';
import { LocalizedCurrencyValue, lt } from '@mews/framework';
import { EnterpriseLink, LocalizedDateTimeFromUtc, TruncatedText } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { Link } from '@optimus-web/core';
import { compactArray } from '@mews/fp';

const helper = createColumnHelper<CashierPastShift>();

export const useColumns = (cashierId: string) => {
    const { data } = useRepositorySuspenseQuery('cashiers', 'getAll', { cashierIds: [cashierId] });

    const enabledCurrencies = data.cashiers?.[0]?.enabledCurrencies?.map((currency) => currency.code) || [];

    const map = enabledCurrencies.map((currencyCode) => {
        if (!currencyCode) return null;

        return helper.accessor(
            (row) => row.shiftBalances.find((it) => it.currencyCode === currencyCode)?.balance || dummyAmount,
            {
                id: currencyCode,
                header: () => <div>{lt('CashierShiftTableBalanceHeading', { currency: currencyCode })}</div>,
                cell: ({ getValue }) => <LocalizedCurrencyValue value={getValue()} />,
                footer: (props) => props.column.id,
            }
        );
    });

    return [
        helper.accessor('startUtc', {
            header: () => lt('Start'),
            cell: ({ getValue }) => <LocalizedDateTimeFromUtc utcString={getValue()} />,
        }),
        helper.accessor('endUtc', {
            header: () => lt('Closed'),
            cell: ({ getValue }) => {
                const value = getValue();
                return value ? <LocalizedDateTimeFromUtc utcString={value} /> : UnicodeEntity.Dash;
            },
        }),
        helper.accessor('closer', {
            header: () => lt('CashierShiftCloser'),
            cell: ({ getValue }) => getValue()?.name,
        }),
        helper.accessor('paymentReportLink', {
            header: () => lt('PaymentReport'),
            cell: ({ getValue }) => (
                <Link variant="basic" target="_blank" href={getValue()} rel="noreferrer">
                    {lt('PaymentReport')}
                </Link>
            ),
        }),
        helper.accessor('notes', {
            header: () => lt('Notes'),
            cell: ({ getValue }) => {
                const note = getValue();
                return note ? <TruncatedText text={note} maxLength={30} /> : UnicodeEntity.Dash;
            },
        }),
        ...compactArray(map),
        helper.display({
            id: 'actions',
            cell: ({ row }) => (
                <EnterpriseLink
                    variant="basic"
                    to="Finance/Cashier/PastShift/$shiftId"
                    params={{
                        shiftId: row.original.shiftId,
                    }}
                    search={{
                        cashierId,
                    }}
                >
                    {lt('ViewDetails')}
                </EnterpriseLink>
            ),
        }),
    ];
};
